import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { SessionService } from '@core/services/use-cases/session.service';
import { UserRole } from '@core/entities/models/role.model';
import { AppRoutes } from '@core/constants/app.routes';

/**
 * Servicio para manejar la redirección automática según el rol del usuario
 */
@Injectable({
  providedIn: 'root',
})
export class RoleRedirectService {
  private readonly router = inject(Router);
  private readonly sessionService = inject(SessionService);

  /**
   * Redirige al usuario a la vista scoped (home) correspondiente según su rol:
   * lista de países, de partners de su país, o de beneficiarios de su partner.
   */
  async redirectToHome(): Promise<boolean> {
    const currentUser = this.sessionService.getCurrentUser();

    if (!currentUser) {
      return this.router.navigate(['/', AppRoutes.auth, AppRoutes.login]);
    }

    const redirectUrl = this.getRedirectUrlForRole(currentUser.role);
    return this.router.navigate(redirectUrl);
  }

  /**
   * Obtiene la URL de redirección para cada rol
   */
  private getRedirectUrlForRole(role: UserRole): string[] {
    const currentUser = this.sessionService.getCurrentUser();

    switch (role) {
      case UserRole.ADMIN:
        return ['/', AppRoutes.admin, AppRoutes.countries];

      case UserRole.COUNTRY_ADMIN:
        // Obtener countryId de los datos del usuario
        const countryId = currentUser?.country?.id;
        if (countryId) {
          return ['/', AppRoutes.admin, AppRoutes.countries, countryId, AppRoutes.partners];
        }
        // Fallback si no hay countryId
        return ['/', AppRoutes.admin, AppRoutes.countries];

      case UserRole.PARTNER_USER:
        // Obtener countryId y partnerId de los datos del usuario
        const userCountryId = currentUser?.country?.id;
        const partnerId = currentUser?.partner?.id;
        if (userCountryId && partnerId) {
          return [
            '/',
            AppRoutes.admin,
            AppRoutes.countries,
            userCountryId,
            AppRoutes.partners,
            partnerId,
            AppRoutes.beneficiaries,
          ];
        }
        // Fallback si no hay IDs
        return ['/', AppRoutes.admin, AppRoutes.countries];

      default:
        return ['/', AppRoutes.auth, AppRoutes.login];
    }
  }

  /**
   * Construye la URL del dashboard según el rol y el scope (país/partner) del usuario actual.
   */
  getDashboardUrl(): string[] {
    const currentUser = this.sessionService.getCurrentUser();

    if (!currentUser) {
      return ['/', AppRoutes.auth, AppRoutes.login];
    }

    const countryId = currentUser.country?.id;
    const partnerId = currentUser.partner?.id;

    if (countryId && partnerId) {
      return [
        '/',
        AppRoutes.admin,
        AppRoutes.countries,
        countryId,
        AppRoutes.partners,
        partnerId,
        AppRoutes.dashboard,
      ];
    }

    if (countryId) {
      return ['/', AppRoutes.admin, AppRoutes.countries, countryId, AppRoutes.dashboard];
    }

    return ['/', AppRoutes.admin, AppRoutes.dashboard];
  }

  /**
   * Obtiene el ID del país asignado al usuario
   */
  private getUserCountryId(): string {
    const currentUser = this.sessionService.getCurrentUser();
    return currentUser?.country?.id || '';
  }

  /**
   * Obtiene el ID del partner asignado al usuario
   */
  private getUserPartnerId(): string {
    const currentUser = this.sessionService.getCurrentUser();
    return currentUser?.partner?.id || '';
  }

  /**
   * Construye la URL para navegar a un nivel específico de la jerarquía
   */
  buildNavigationUrl(level: 'country' | 'partner', id: string, countryId?: string): string[] {
    const currentUser = this.sessionService.getCurrentUser();
    if (!currentUser) {
      return ['/', AppRoutes.auth, AppRoutes.login];
    }

    switch (level) {
      case 'country':
        return ['/', AppRoutes.admin, AppRoutes.countries, id, AppRoutes.partners];
      case 'partner':
        // Nueva ruta anidada: /admin/countries/:countryId/partners/:partnerId/beneficiaries
        if (countryId) {
          return [
            '/',
            AppRoutes.admin,
            AppRoutes.countries,
            countryId,
            AppRoutes.partners,
            id,
            AppRoutes.beneficiaries,
          ];
        } else {
          // Si no hay countryId, redirigir al home del usuario según su rol
          return this.getRedirectUrlForRole(currentUser.role);
        }
        break;
      default:
        return this.getRedirectUrlForRole(currentUser.role);
    }
  }
}
